// Énumérations métier partagées (spec 03). Source unique pour le schéma, Zod et le moteur.

/** Date calendaire `YYYY-MM-DD`, interprétée dans le fuseau du tenant. */
export type IsoDate = string;

export const PERIODICITIES = ['annual', 'semiannual', 'quarterly', 'monthly', 'single'] as const;
export type Periodicity = (typeof PERIODICITIES)[number];

export const CONTRACT_STATUSES = ['active', 'suspended', 'lapsed', 'cancelled', 'expired'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const INSTALLMENT_STATUSES = [
  'upcoming',
  'due',
  'overdue',
  'partial',
  'paid',
  'waived',
  'cancelled',
] as const;
export type InstallmentStatus = (typeof INSTALLMENT_STATUSES)[number];

export const INSTALLMENT_SOURCES = ['computed', 'imported', 'api'] as const;
export type InstallmentSource = (typeof INSTALLMENT_SOURCES)[number];

export const PRODUCT_FAMILIES = ['auto', 'health', 'home', 'life', 'business', 'other'] as const;
export type ProductFamily = (typeof PRODUCT_FAMILIES)[number];

export const CHANNELS = ['whatsapp', 'sms', 'email'] as const;
export type Channel = (typeof CHANNELS)[number];

/** Préavis proposés avant échéance ou fin de contrat (BA-079) : 1, 2 ou 3 mois, sinon libre. */
export const NOTICE_PRESETS = [30, 60, 90] as const;
/** Préavis avant échéance par défaut : l'ancien premier message (information à J-30). */
export const DEFAULT_DUE_NOTICE_DAYS = 30;

export const LANGUAGES = ['fr', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

export const USER_ROLES = ['admin', 'manager', 'agent', 'readonly'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PAYMENT_METHODS = ['kashy', 'agency', 'bank', 'check', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ['pending', 'confirmed', 'failed', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const SCENARIO_STEP_KINDS = ['message', 'escalation'] as const;
export type ScenarioStepKind = (typeof SCENARIO_STEP_KINDS)[number];

export const SEQUENCE_STATUSES = [
  'scheduled',
  'running',
  'paused',
  'stopped_paid',
  'stopped_optout',
  'stopped_manual',
  'escalated',
  'completed',
] as const;
export type SequenceStatus = (typeof SEQUENCE_STATUSES)[number];

export const SEQUENCE_EVENT_KINDS = [
  'planned',
  'sent',
  'delivered',
  'read',
  'failed',
  'fallback',
  'skipped',
  'paused',
  'resumed',
  'stopped',
  'escalated',
  /** Action de l'assuré sur sa page (payer, agence, déjà payé, opposition). */
  'page_action',
] as const;
export type SequenceEventKind = (typeof SEQUENCE_EVENT_KINDS)[number];

/** Nature d'une escalade (spec 05.7, 07, 09). */
export const ESCALATION_KINDS = [
  'unpaid',
  'optout',
  'payment_check',
  'question',
  'overpayment',
  'refund',
  'delivery',
  'renewal',
  'claim',
] as const;
export type EscalationKind = (typeof ESCALATION_KINDS)[number];

export const RENEWAL_STATUSES = ['pending', 'accepted', 'declined', 'cancelled'] as const;
export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];

/** Natures de sinistre déclarables par l'assuré (BA-077) ; jamais de sinistre santé. */
export const CLAIM_KINDS = [
  'accident',
  'theft',
  'fire',
  'water_damage',
  'glass_breakage',
  'other',
] as const;
export type ClaimKind = (typeof CLAIM_KINDS)[number];
export const CLAIM_STATUSES = ['received', 'forwarded', 'closed'] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

export const IMPORT_KINDS = ['contracts', 'installments', 'payments'] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];

/** Connecteur SI en lecture seule (BA-094) : format de la source et mode d'authentification. */
export const CONNECTOR_FORMATS = ['json', 'csv'] as const;
export type ConnectorFormat = (typeof CONNECTOR_FORMATS)[number];
export const CONNECTOR_AUTHS = ['none', 'bearer', 'basic', 'header'] as const;
export type ConnectorAuth = (typeof CONNECTOR_AUTHS)[number];
/** Intervalles de lecture proposés, en minutes. */
export const CONNECTOR_INTERVALS = [15, 60, 360, 1440] as const;
